import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  ClipboardList, ExternalLink, Users, Plus, Trash2, ChevronDown, ChevronUp,
  Mail, Send, Search, Calendar, Copy, Pencil, X, Check
} from "lucide-react";
import AssessmentResultsView from "../AssessmentResultsView";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ASSESSMENT_LINK = window.location.origin + "/assess/paladin-maxsc-2026";

const METRIC_NAMES = {
  sj: "Squat Jump", vj: "Vertical Jump", bj: "Broad Jump", rsi: "RSI", ag: "Approach Gain"
};

const SPORTS = [
  "Basketball", "Volleyball", "Football", "Soccer", "Baseball/Softball",
  "Track & Field", "Tennis", "Swimming", "Lacrosse", "Hockey", "Cheerleading",
  "Gymnastics", "Wrestling", "Cross Country", "Other"
];

const GRADES = [
  "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th", "college", "pro"
];

const AssessmentsManager = () => {
  const [subTab, setSubTab] = useState("results");
  const [reports, setReports] = useState([]);
  const [accessEmails, setAccessEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailsError, setEmailsError] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sendingId, setSendingId] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const emailInputRef = useRef(null);

  useEffect(() => {
    fetchReports();
    fetchAccessEmails();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await fetch(`${API}/jump-assessment/reports`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (err) {
      console.error("Failed to fetch reports", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessEmails = async () => {
    try {
      setEmailsError(false);
      const res = await fetch(`${API}/jump-assessment/access-emails`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAccessEmails(data.emails || []);
      } else {
        const errData = await res.json().catch(() => null);
        console.error("Failed to fetch access emails:", res.status, errData);
        setEmailsError(true);
        if (res.status === 403) {
          toast.error("Admin access required to manage coach emails");
        }
      }
    } catch (err) {
      console.error("Failed to fetch access emails", err);
      setEmailsError(true);
    }
  };

  // ==================== EMAIL MANAGEMENT ====================
  const addEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (accessEmails.some(e => e.email === email)) {
      toast.error("Email already added");
      setNewEmail("");
      return;
    }
    try {
      const res = await fetch(`${API}/jump-assessment/access-emails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        toast.success(`Added ${email}`);
        setNewEmail("");
        fetchAccessEmails();
        emailInputRef.current?.focus();
      } else {
        const err = await res.json().catch(() => null);
        toast.error(err?.detail || `Failed to add email (${res.status})`);
      }
    } catch (e) {
      console.error("Add email error:", e);
      toast.error("Network error — failed to add email");
    }
  };

  const removeEmail = async (email) => {
    try {
      const res = await fetch(`${API}/jump-assessment/access-emails`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        toast.success("Email removed");
        fetchAccessEmails();
      }
    } catch {
      toast.error("Failed to remove email");
    }
  };

  const updateCapabilities = async (email, capabilities) => {
    try {
      const res = await fetch(`${API}/jump-assessment/access-emails/capabilities`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, capabilities }),
      });
      if (res.ok) {
        toast.success("Permissions updated");
        fetchAccessEmails();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update");
      }
    } catch {
      toast.error("Failed to update permissions");
    }
  };

  // ==================== ASSESSMENT ACTIONS ====================
  const resendReport = async (assessmentId) => {
    setSendingId(assessmentId);
    try {
      const res = await fetch(`${API}/jump-assessment/send-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ assessment_id: assessmentId, access_token: "paladin-maxsc-2026", resend: true }),
      });
      let data;
      try { data = await res.json(); } catch { data = {}; }
      if (res.ok) {
        toast.success(data.message || "Report sent!");
        fetchReports();
      } else {
        toast.error(data.detail || "Failed to send");
      }
    } catch {
      toast.error("Failed to send report");
    } finally {
      setSendingId(null);
    }
  };

  const duplicateReport = async (assessmentId) => {
    try {
      const res = await fetch(`${API}/jump-assessment/${assessmentId}/duplicate`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Assessment duplicated");
        fetchReports();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Duplicate failed");
      }
    } catch {
      toast.error("Failed to duplicate");
    }
  };

  const deleteReport = async (assessmentId) => {
    if (!window.confirm("Delete this assessment? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/jump-assessment/${assessmentId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Assessment deleted");
        setExpandedId(null);
        fetchReports();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Delete failed");
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  const saveEdit = async () => {
    if (!editingReport) return;
    try {
      const res = await fetch(`${API}/jump-assessment/${editingReport.assessment_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editingReport),
      });
      if (res.ok) {
        toast.success("Assessment updated");
        setEditingReport(null);
        fetchReports();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Update failed");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const filteredReports = reports.filter(r =>
    !searchQuery ||
    (r.athlete_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.parent_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.sport || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getDomColor = (val) => {
    if (val >= 100) return "text-green-400";
    if (val >= 75) return "text-amber-400";
    return "text-red-400";
  };

  const subTabs = [
    { id: "results", label: "Athlete Results", icon: ClipboardList },
    { id: "access", label: "Coaches with Access", icon: Users },
  ];

  return (
    <div className="space-y-4" data-testid="assessments-manager">
      {/* Assessment Link Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-blue-300">Run Assessment</h3>
          <p className="text-xs text-slate-400 mt-1">Share this link with anyone who needs to run a jump test</p>
        </div>
        <a
          href={ASSESSMENT_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
          data-testid="open-assessment-link"
        >
          <ExternalLink className="w-4 h-4" />
          Open Assessment Form
        </a>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2" data-testid="assessment-subtabs">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              subTab === tab.id
                ? "bg-blue-600 text-white"
                : "bg-[#1E293B] text-slate-400 hover:text-white hover:bg-[#2D3B4F]"
            }`}
            data-testid={`subtab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ==================== ATHLETE RESULTS ==================== */}
      {subTab === "results" && (
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search by athlete name, parent, or sport..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-[#141B2D] border border-[#1E293B] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
              data-testid="search-reports"
            />
          </div>

          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No assessments yet</p>
              <p className="text-slate-500 text-sm mt-1">Results will appear here after running assessments</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredReports.map(report => {
                const expanded = expandedId === report.assessment_id;
                const isEditing = editingReport?.assessment_id === report.assessment_id;
                return (
                  <div key={report.assessment_id} className="bg-[#141B2D] border border-[#1E293B] rounded-xl overflow-hidden" data-testid={`report-${report.assessment_id}`}>
                    {/* Summary Row */}
                    <button
                      onClick={() => { setExpandedId(expanded ? null : report.assessment_id); setEditingReport(null); }}
                      className="w-full flex items-center gap-3 p-4 sm:p-5 text-left hover:bg-[#1E293B]/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-white text-base md:text-lg">{report.athlete_name}</span>
                          <span className="text-sm text-slate-300 capitalize">{report.age_grade} &middot; {report.sport}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-sm font-semibold text-emerald-300">Stage: {report.stage_score}%</span>
                          <span className="text-sm font-semibold text-blue-300">Elite: {report.elite_score}%</span>
                          {report.primary_limiter && (
                            <span className="text-sm font-semibold text-red-400">Limiter: {METRIC_NAMES[report.primary_limiter] || report.primary_limiter}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="text-right hidden sm:block">
                          <p className="text-sm text-slate-300">
                            <Calendar className="w-3.5 h-3.5 inline mr-1" />
                            {new Date(report.created_at).toLocaleDateString()}
                          </p>
                          {report.email_sent && <p className="text-xs font-semibold text-green-400 mt-0.5">Email sent</p>}
                        </div>
                        {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                      </div>
                    </button>

                    {/* Expanded Detail */}
                    {expanded && (
                      <div className="border-t border-[#1E293B] p-3 sm:p-4 space-y-4 bg-[#0f172a]/50">

                        {/* Edit Mode */}
                        {isEditing ? (
                          <EditForm
                            data={editingReport}
                            onChange={setEditingReport}
                            onSave={saveEdit}
                            onCancel={() => setEditingReport(null)}
                          />
                        ) : (
                          <AssessmentResultsView report={report} />
                        )}

                        {/* Actions */}
                        {!isEditing && (
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => setEditingReport({ ...report })}
                              className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                              data-testid={`edit-${report.assessment_id}`}>
                              <Pencil className="w-3 h-3" /> Edit
                            </button>
                            <button onClick={() => duplicateReport(report.assessment_id)}
                              className="flex items-center gap-1.5 bg-[#1E293B] hover:bg-[#2D3B4F] text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                              data-testid={`duplicate-${report.assessment_id}`}>
                              <Copy className="w-3 h-3" /> Duplicate
                            </button>
                            <button onClick={() => resendReport(report.assessment_id)}
                              disabled={sendingId === report.assessment_id}
                              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                              data-testid={`resend-${report.assessment_id}`}>
                              <Send className="w-3 h-3" />
                              {sendingId === report.assessment_id ? "Sending..." : report.email_sent ? "Re-send Email" : "Send Email"}
                            </button>
                            <a href={`mailto:${report.parent_email}`}
                              className="flex items-center gap-1.5 bg-[#1E293B] hover:bg-[#2D3B4F] text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                              <Mail className="w-3 h-3" /> Email Parent
                            </a>
                            <button onClick={() => deleteReport(report.assessment_id)}
                              className="flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ml-auto"
                              data-testid={`delete-${report.assessment_id}`}>
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ==================== COACHES WITH ACCESS ==================== */}
      {subTab === "access" && (
        <div className="space-y-4">
          {/* Login Link Banner */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-amber-300">Coach Login Portal</h3>
            <p className="text-xs text-slate-400 mt-1">Coaches log in at this URL to view assessment results:</p>
            <div className="flex items-center gap-2 mt-2">
              <code className="text-xs text-white bg-[#0f172a] px-3 py-1.5 rounded-lg flex-1 truncate"
                data-testid="coach-portal-url">{window.location.origin}/assessments/login</code>
              <button
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/assessments/login`); toast.success("Link copied!"); }}
                className="bg-amber-600 hover:bg-amber-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                data-testid="copy-portal-link-btn"
              >
                <Copy className="w-3 h-3 inline mr-1" /> Copy
              </button>
            </div>
          </div>

          <div className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-4">
            <h3 className="text-sm font-semibold text-white mb-1">Add Coach Emails</h3>
            <p className="text-xs text-slate-500 mb-3">Type an email and press Enter or click Add. Add as many as you need.</p>
            <div className="flex gap-2">
              <input
                ref={emailInputRef}
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                placeholder="coach@example.com"
                className="flex-1 bg-[#0f172a] border border-[#1E293B] rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
                data-testid="add-email-input"
              />
              <button
                onClick={addEmail}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                data-testid="add-email-btn"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>

          {emailsError ? (
            <div className="text-center py-8 space-y-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <X className="w-5 h-5 text-red-400" />
              </div>
              <p className="text-slate-400 text-sm">Failed to load coach emails</p>
              <button
                data-testid="retry-access-emails-btn"
                onClick={fetchAccessEmails}
                className="px-4 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          ) : accessEmails.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No coaches added yet</p>
              <p className="text-slate-500 text-xs mt-1">Only you (admin) can view results right now</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 px-1">{accessEmails.length} coach{accessEmails.length !== 1 ? "es" : ""} with access</p>
              {accessEmails.map((item) => (
                <CoachEmailRow
                  key={item.email}
                  item={item}
                  onRemove={removeEmail}
                  onCapabilitiesChange={updateCapabilities}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== INLINE EDIT FORM ====================
function EditForm({ data, onChange, onSave, onCancel }) {
  const update = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div className="space-y-3" data-testid="edit-form">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <EditField label="Athlete Name" value={data.athlete_name} onChange={v => update("athlete_name", v)} />
        <EditField label="Parent Name" value={data.parent_name} onChange={v => update("parent_name", v)} />
        <EditField label="Parent Email" value={data.parent_email} onChange={v => update("parent_email", v)} type="email" />
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">Gender</label>
          <select value={data.gender || "male"} onChange={e => update("gender", e.target.value)}
            className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none">
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">Grade</label>
          <select value={data.age_grade} onChange={e => update("age_grade", e.target.value)}
            className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none">
            {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">Sport</label>
          <select value={data.sport} onChange={e => update("sport", e.target.value)}
            className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none">
            {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <EditField label="Bodyweight" value={data.bodyweight} onChange={v => update("bodyweight", parseFloat(v) || 0)} type="number" />
        <EditField label="Height (in)" value={data.height_inches} onChange={v => update("height_inches", parseFloat(v) || 0)} type="number" />
        <EditField label="Squat Jump" value={data.squat_jump} onChange={v => update("squat_jump", parseFloat(v) || 0)} type="number" />
        <EditField label="Vertical Jump" value={data.vertical_jump} onChange={v => update("vertical_jump", parseFloat(v) || 0)} type="number" />
        <EditField label="Broad Jump" value={data.broad_jump} onChange={v => update("broad_jump", parseFloat(v) || 0)} type="number" />
        <EditField label="RSI" value={data.rsi} onChange={v => update("rsi", parseFloat(v) || 0)} type="number" />
        <EditField label="Approach Jump" value={data.approach_jump} onChange={v => update("approach_jump", parseFloat(v) || 0)} type="number" />
        <EditField label="Reach (in)" value={data.reach_inches || ""} onChange={v => update("reach_inches", parseFloat(v) || null)} type="number" />
        <EditField label="Touch (in)" value={data.approach_touch_inches || ""} onChange={v => update("approach_touch_inches", parseFloat(v) || null)} type="number" />
        <EditField label="Iso Squat" value={data.iso_squat_force || ""} onChange={v => update("iso_squat_force", parseFloat(v) || null)} type="number" />
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onSave}
          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors"
          data-testid="save-edit-btn">
          <Check className="w-3.5 h-3.5" /> Save & Recalculate
        </button>
        <button onClick={onCancel}
          className="flex items-center gap-1.5 bg-[#1E293B] hover:bg-[#2D3B4F] text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors"
          data-testid="cancel-edit-btn">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] text-slate-500 mb-0.5">{label}</label>
      <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none focus:border-blue-500"
        step={type === "number" ? "0.01" : undefined}
      />
    </div>
  );
}

const CAP_OPTIONS = [
  { key: "edit", label: "Edit" },
  { key: "duplicate", label: "Duplicate" },
  { key: "delete", label: "Delete" },
];

function formatLastAccessed(isoStr) {
  if (!isoStr) return null;
  const date = new Date(isoStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function CoachEmailRow({ item, onRemove, onCapabilitiesChange }) {
  const caps = item.capabilities || ["view"];
  const lastAccessed = formatLastAccessed(item.last_accessed);

  const toggleCap = (cap) => {
    const newCaps = caps.includes(cap)
      ? caps.filter(c => c !== cap)
      : [...caps, cap];
    onCapabilitiesChange(item.email, newCaps);
  };

  return (
    <div className="bg-[#141B2D] border border-[#1E293B] rounded-lg px-4 py-3" data-testid={`access-email-${item.email}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Mail className="w-4 h-4 text-blue-400" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-white">{item.email}</span>
            <span
              data-testid={`last-accessed-${item.email}`}
              className={`text-[10px] mt-0.5 ${lastAccessed ? "text-emerald-400" : "text-slate-600"}`}
            >
              {lastAccessed ? `Last active ${lastAccessed}` : "Never accessed"}
            </span>
          </div>
        </div>
        <button
          onClick={() => onRemove(item.email)}
          className="text-slate-500 hover:text-red-400 transition-colors p-1"
          data-testid={`remove-email-${item.email}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-3 mt-2 ml-11">
        <span className="text-[10px] text-slate-500 uppercase tracking-wide">Permissions:</span>
        <span className="text-[10px] text-green-400 bg-green-500/10 px-2 py-0.5 rounded">View</span>
        {CAP_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => toggleCap(key)}
            className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
              caps.includes(key)
                ? "bg-blue-500/20 text-blue-300"
                : "bg-[#0f172a] text-slate-500 hover:text-slate-300"
            }`}
            data-testid={`toggle-${key}-${item.email}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default AssessmentsManager;
